export const MAX_PROOF_BYTES = 2 * 1024 * 1024;
/** Firestore doc limit is 1 MiB — keep base64 string under this. */
export const MAX_FIRESTORE_PROOF_CHARS = 750_000;
export const ACCEPTED_PROOF_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function validatePaymentProofFile(file) {
  if (!file) return { error: 'Please upload a payment screenshot.' };
  if (!ACCEPTED_PROOF_TYPES.includes(file.type)) {
    return { error: 'Only JPG, PNG, or WebP images are allowed.' };
  }
  if (file.size > MAX_PROOF_BYTES) {
    return { error: 'Image must be under 2 MB.' };
  }
  return null;
}

/** Resize/compress screenshots (Firestore Spark plan — no Storage). */
export function compressPaymentProof(
  file,
  maxWidth = 1280,
  quality = 0.82,
  force = false
) {
  if (!file || (!force && file.size < 400_000)) {
    return Promise.resolve(file);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob(
        blob => {
          if (!blob) {
            resolve(file);
            return;
          }
          resolve(
            new File(
              [blob],
              file.name.replace(
                /\.\w+$/,
                mime === 'image/png' ? '.png' : '.jpg'
              ),
              {
                type: mime,
                lastModified: Date.now(),
              }
            )
          );
        },
        mime,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image file.'));
    };

    img.src = url;
  });
}

/** Convert image to a compressed data URL stored in Firestore (works on Spark / no Storage). */
export async function fileToFirestoreProofDataUrl(file) {
  const compressed = await compressPaymentProof(file, 800, 0.72, true);

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read image file.'));
    reader.readAsDataURL(compressed);
  });

  if (
    typeof dataUrl !== 'string' ||
    dataUrl.length > MAX_FIRESTORE_PROOF_CHARS
  ) {
    throw new Error(
      'Screenshot is too large. Please upload a smaller or clearer image (under ~500 KB).'
    );
  }

  return dataUrl;
}
